<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

// Edits a stage's named, role-gated transition actions, stored as
// [{ action, next_stage_key, roles: [], require_documents }]. `action` is the verb the staff member
// invokes; `next_stage_key` points at a sibling stage; `roles` restricts who may perform it (empty =
// anyone); `require_documents` blocks the move until required documents are accepted.
const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  stages: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const ROLE_KEYS = ['administrator', 'agent'];

const rows = computed({
  get: () => props.modelValue || [],
  set: value => emit('update:modelValue', value),
});

function addRow() {
  rows.value = [
    ...rows.value,
    { action: '', next_stage_key: '', roles: [], require_documents: false, require_payment: false, require_forms: false },
  ];
}

function updateRow(index, patch) {
  rows.value = rows.value.map((row, idx) =>
    idx === index ? { ...row, ...patch } : row
  );
}

function removeRow(index) {
  rows.value = rows.value.filter((_, idx) => idx !== index);
}

function hasRole(row, role) {
  return Array.isArray(row.roles) && row.roles.includes(role);
}

function toggleRole(index, role) {
  const row = rows.value[index];
  const current = Array.isArray(row.roles) ? row.roles : [];
  const roles = current.includes(role)
    ? current.filter(r => r !== role)
    : [...current, role];
  updateRow(index, { roles });
}
</script>

<template>
  <div class="flex flex-col gap-2">
    <div class="flex flex-col gap-0.5">
      <h4 class="text-xs font-semibold tracking-wide uppercase text-n-slate-11">
        {{ t('TRAMITE_TYPES.ACTIONS_EDITOR.TITLE') }}
      </h4>
      <p class="text-xs text-n-slate-10">
        {{ t('TRAMITE_TYPES.ACTIONS_EDITOR.HELP') }}
      </p>
    </div>

    <p v-if="!rows.length" class="py-2 text-xs text-n-slate-10">
      {{ t('TRAMITE_TYPES.ACTIONS_EDITOR.EMPTY') }}
    </p>

    <div
      v-for="(row, index) in rows"
      :key="index"
      class="flex flex-col gap-2 p-2 rounded-md border border-n-weak/60"
    >
      <div class="flex flex-wrap gap-2 items-center">
        <input
          :value="row.action"
          type="text"
          :placeholder="t('TRAMITE_TYPES.ACTIONS_EDITOR.ACTION_PLACEHOLDER')"
          class="flex-1 px-2 h-8 text-sm rounded-md border min-w-32 border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-none focus:border-n-brand"
          @input="updateRow(index, { action: $event.target.value })"
        />
        <select
          :value="row.next_stage_key"
          class="px-2 h-8 text-sm rounded-md border w-44 border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-none focus:border-n-brand"
          @change="updateRow(index, { next_stage_key: $event.target.value })"
        >
          <option value="">
            {{ t('TRAMITE_TYPES.ACTIONS_EDITOR.NEXT_STAGE_NONE') }}
          </option>
          <option
            v-for="stage in stages"
            :key="stage.stage_key"
            :value="stage.stage_key"
          >
            {{ stage.name || stage.stage_key }}
          </option>
        </select>
        <Button
          type="button"
          variant="ghost"
          color="ruby"
          size="xs"
          icon="i-lucide-x"
          @click="removeRow(index)"
        />
      </div>

      <div class="flex flex-wrap gap-3 items-center pl-1">
        <span class="text-xs text-n-slate-10">
          {{ t('TRAMITE_TYPES.ACTIONS_EDITOR.ROLES') }}:
        </span>
        <label
          v-for="role in ROLE_KEYS"
          :key="role"
          class="flex gap-1 items-center text-xs cursor-pointer text-n-slate-11"
        >
          <input
            type="checkbox"
            :checked="hasRole(row, role)"
            @change="toggleRole(index, role)"
          />
          {{ t(`TRAMITE_TYPES.ACTIONS_EDITOR.ROLE_OPTIONS.${role}`) }}
        </label>
        <label
          class="flex gap-1 items-center ml-2 text-xs cursor-pointer text-n-slate-11"
        >
          <input
            type="checkbox"
            :checked="row.require_documents === true"
            @change="updateRow(index, { require_documents: $event.target.checked })"
          />
          {{ t('TRAMITE_TYPES.ACTIONS_EDITOR.REQUIRE_DOCUMENTS') }}
        </label>
        <label
          class="flex gap-1 items-center text-xs cursor-pointer text-n-slate-11"
        >
          <input
            type="checkbox"
            :checked="row.require_payment === true"
            @change="updateRow(index, { require_payment: $event.target.checked })"
          />
          {{ t('TRAMITE_TYPES.ACTIONS_EDITOR.REQUIRE_PAYMENT') }}
        </label>
        <label
          class="flex gap-1 items-center text-xs cursor-pointer text-n-slate-11"
        >
          <input
            type="checkbox"
            :checked="row.require_forms === true"
            @change="updateRow(index, { require_forms: $event.target.checked })"
          />
          {{ t('TRAMITE_TYPES.ACTIONS_EDITOR.REQUIRE_FORMS') }}
        </label>
      </div>
    </div>

    <div>
      <Button
        type="button"
        variant="ghost"
        color="slate"
        size="xs"
        icon="i-lucide-plus"
        :label="t('TRAMITE_TYPES.ACTIONS_EDITOR.ADD')"
        @click="addRow"
      />
    </div>
  </div>
</template>
